import React, { Component } from 'react';
import { connect } from 'react-redux';
import { back } from 'redux-first-router';
import styled, { css } from 'styled-components';

import i18n from 'i18n';
import { Link } from 'components/Common';

import { navigateOut } from 'utils';

import {
  androidNavbarTopRowHeight,
  androidNavbarBottomRowHeight,
  androidNavbarHeight
} from 'themes';
import { isiOS } from 'config';

import iOSArrowBack from 'icons/ios-arrow-back.svg';
import androidArrowBack from 'icons/android-arrow-back.svg';

const iosNavCenterWidth = ({ centerPriority }) =>
  `max-width: ${centerPriority ? '70' : '50'}vw;`;
const iosNavSidesWidth = ({ centerPriority }) =>
  `min-width: ${centerPriority ? '15' : '25'}%;`;

export const NavCenterContainer = styled.div`
  display: flex;
  line-height: 1;
  text-align: center;
  ${isiOS
    ? css`
        background: #f3f3f3;
        border-radius: 30px;
        ${iosNavCenterWidth}
      `
    : ''}
`;

const NavLink = ({
  to,
  redirect,
  className,
  children,
  pageDirection,
  active: _active, // unused
  ...props
}) => (
  <div className={className}>
    <Link to={to} redirect={redirect} pageDirection={pageDirection} {...props}>
      {children}
    </Link>
  </div>
);

const iOSLineHeight = 28;

export const NavCenterItem = styled(NavLink)`
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.5;
  width: 50%;
  vertical-align: middle;
  a {
    display: block;
    width: 100%;
  }
  ${({ active, theme }) =>
    isiOS
      ? `
    color: #9B9B9B;
    line-height: ${iOSLineHeight + 2}px;
    ${
      active
        ? `
      line-height: ${iOSLineHeight}px;
      background: white;
      border: 1px solid #f3f3f3;
      border-radius: ${iOSLineHeight}px;
      color: #01b1ba;
    `
        : ''
    }
    a {
      font-family: ${theme.fontSemibold};
      font-size: 14px;
    }
  `
      : `
    color: black;
    height: ${androidNavbarBottomRowHeight - 1}px;
    line-height: ${androidNavbarBottomRowHeight - 1}px;
    text-transform: uppercase;
    border-bottom: 0.5px solid #D4D4D4;
    padding-top: 1px;
    ${
      active
        ? `
      color: #01b1ba;
      border-bottom: 2px solid ${theme.fitbitGreen};
    `
        : ''
    }
    a {
      font: 14px ${theme.fontSemibold};
      line-height: 49px;
    }
  `};
`;

const Back = styled.div`
  ${isiOS
    ? `
    position: relative;
    padding-left: 27px;
    min-height: 44px;

    &:before {
      position: absolute;
      top: 12px;
      left: 8px;
      width: 12px;
      height: 20px;
      content: ' ';
      background-image: url(${iOSArrowBack});
      background-size: contain;
      -webkit-transform: translateZ(0);
  `
    : `
    padding-left: 20px;
    &:before {
      position: absolute;
      top: 15px;
      left: 16px;
      width: 24px;
      height: 24px;
      content: ' ';
      background-image: url(${androidArrowBack});
    }
  `}
`;

const BackLink = ({ children, ...props }) => <Back {...props}>{children}</Back>;

const BaseNavItem = styled.div`
  display: inline-block;
`;

const NavLeft = styled(BaseNavItem)`
  ${isiOS
    ? iosNavSidesWidth
    : `
    width: 72px;
    .back-text {
      display: none;
    }
  `}
`;

const NavCenter = styled(BaseNavItem)`
  flex-grow: 1;
  ${({ theme }) =>
    isiOS
      ? `
    text-align: center;
  `
      : `
    font: 20px ${theme.fontSemibold};
    span {
      display: block;
      margin: 15px 0;
    }
  `}
`;

const NavRight = styled(BaseNavItem)`
  text-align: right;
  ${isiOS
    ? iosNavSidesWidth
    : `
    width: 20%;
  `}
`;

const Navbar = styled.div.attrs({
  className: 'navbar'
})`
  position: absolute;
  top: 0;
  width: 100%;
  display: flex;
  color: ${({ theme }) => theme.fitbitGreen};
  background-color: white;
  z-index: 65535;
  ${({ theme }) =>
    isiOS
      ? `
    align-items: center;
    border-bottom: 1px solid ${theme.borderColor};
    box-sizing: border-box;
    font-size: 18px;
    height: ${theme.NavbarHeight};
    left: 0;
    line-height: ${theme.NavbarHeight};
  `
      : `
    min-height: ${androidNavbarTopRowHeight}px;
    height: ${androidNavbarTopRowHeight}px;
    line-height: ${androidNavbarTopRowHeight}px;
    &.scrolling {
      box-shadow: 0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12);
    }
    a {
      font-family: ${theme.fontSemibold};
    }
  `}
`;

const AndroidNavBar = styled(Navbar).attrs({
  className: 'nav-bar'
})`
  flex-direction: column;
  z-index: 65535;
  ${({ top = 0 }) => `
    ${({ top }) =>
      top > 0 &&
      `
      box-shadow:
        0 2px 4px -1px rgba(0, 0, 0, 0.2),
        0 4px 5px 0px rgba(0, 0, 0, 0.14),
        0 1px 10px 0px rgba(0, 0, 0, 0.12);
    `};
    line-height: ${androidNavbarHeight(top)};
    min-height: calc(${androidNavbarHeight(top)});
    transform: translate3d(0, -${top}px, 0);
  `}
`;

const AndroidTopRow = styled.div`
  align-items: center;
  display: flex;
  height: ${androidNavbarTopRowHeight}px;
  width: 100vw;
`;

const AndroidBottomRow = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  height: ${androidNavbarBottomRowHeight}px;
  > * {
    flex-grow: 1;
    > * {
      line-height: ${androidNavbarBottomRowHeight - 2}px;
    }
  }
`;

class Navigation extends Component {
  onNavigate = () => {
    const { backNavigate, directLink } = this.props;
    if (typeof directLink !== 'undefined') {
      navigateOut();
      return;
    }
    if (backNavigate) {
      backNavigate();
      return;
    }
    back();
  };

  renderBack() {
    const { directLink } = this.props;
    let { backText } = this.props;

    // If direct link default to "Back" localized since we exit the gallery
    // on back navigation with direct links
    if (typeof directLink !== 'undefined') {
      backText = i18n('component.Navigation.directLinkBackText')();
    }

    if (typeof backText === 'undefined') {
      return;
    }

    return (
      <BackLink data-test-type="back-link" onClick={this.onNavigate}>
        <span className="back-text">{`  ${backText}`}</span>
      </BackLink>
    );
  }

  renderRight = () => this.props.right || null;

  render() {
    const { center, scrolling, top, tabs, children } = this.props;

    if (children) {
      return (
        <Navbar className={scrolling ? 'scrolling' : ''}>{children}</Navbar>
      );
    }

    if (isiOS || !tabs) {
      const centerItem = tabs || <span>{center}</span>;
      const centerPriority = !!tabs;
      return (
        <Navbar className={scrolling ? 'scrolling' : ''}>
          <NavLeft centerPriority={centerPriority}>{this.renderBack()}</NavLeft>
          <NavCenter className="nav-center">{centerItem}</NavCenter>
          <NavRight centerPriority={centerPriority} className="nav-right">
            {this.renderRight()}
          </NavRight>
        </Navbar>
      );
    }

    return (
      <AndroidNavBar top={top} className={scrolling ? 'scrolling' : ''}>
        <AndroidTopRow>
          <NavLeft>{this.renderBack()}</NavLeft>
          <NavCenter>{center}</NavCenter>
          <NavRight>{this.renderRight()}</NavRight>
        </AndroidTopRow>
        <AndroidBottomRow>{tabs}</AndroidBottomRow>
      </AndroidNavBar>
    );
  }
}

const mapStateToProps = ({ location: { query = {} } }) => ({
  directLink: query.directLink
});

export default connect(mapStateToProps)(Navigation);
